import { ApiKey, ApiKeyDisabledReason } from "../../state/types";
// ============================================
// API Key State Utilities
// ============================================

/**
 * Check if an API key is revoked (soft-deleted).
 * Revoked keys are NOT visible to users and cannot be recovered.
 * Only the new `revokedAt` timestamp indicates soft-delete.
 *
 * @param {object} apiKey - The API key document
 * @returns {boolean}
 */
function isApiKeyRevoked(apiKey: ApiKey) {
    return !!apiKey?.revokedAt;
}

/**
 * Check if an API key is disabled.
 * Disabled keys ARE visible to users and can be re-enabled.
 * Supports both legacy `revoked: true` and new `disabledAt` timestamp.
 *
 * @param {object} apiKey - The API key document
 * @returns {boolean}
 */
function isApiKeyDisabled(apiKey: ApiKey) {
    return !!(apiKey?.disabledAt || apiKey?.revoked);
}

/**
 * Check if an API key is paused.
 * Paused keys are visible to users and can be re-enabled.
 *
 * @param {object} apiKey - The API key document
 * @returns {boolean}
 */
function isApiKeyPaused(apiKey: ApiKey) {
    return isApiKeyDisabled(apiKey) && apiKey.disabledReason === ApiKeyDisabledReason.FOLDER_PAUSED;
}

/**
 * Check if an API key is active (usable for API calls).
 * A key is active if it's neither revoked nor disabled.
 *
 * @param {object} apiKey - The API key document
 * @returns {boolean}
 */
function isApiKeyActive(apiKey: ApiKey) {
    return !!apiKey && !isApiKeyRevoked(apiKey) && !isApiKeyDisabled(apiKey);
}

/**
 * Check if an API key should be visible to users.
 * Revoked (soft-deleted) keys are hidden; disabled keys are visible.
 *
 * @param {object} apiKey - The API key document
 * @returns {boolean}
 */
function isApiKeyVisibleToUser(apiKey: ApiKey) {
    return !!apiKey && !isApiKeyRevoked(apiKey);
}

/**
 * Check if an API key is a workspace-wide API key.
 * A workspace-wide API key is an API key that is not scoped to any folders.
 *
 * @param {object} apiKey - The API key document
 * @returns {boolean}
 */
function isWorkspaceApiKey(apiKey: ApiKey) {
    if (!apiKey) return false;
    if ((apiKey?.projectGroupIds?.length || 0) > 0) return false;
    return true;
}

/**
 * Generates a prefix from the first N characters of an API key for use in billing records
 * @param {string} apiKey - The API key to generate a prefix from
 * @param {Object} [opts] - Options for generating the prefix
 * @param {number} [opts.length=5] - The number of characters to include in the prefix
 * @returns {string} - The generated prefix (e.g., "abc12") or empty string if invalid
 */
function generateApiKeyPrefix(apiKey: string, opts = { length: 5 }): string {
    if (typeof apiKey !== "string" || apiKey.includes(":")) {
        return "";
    }

    let truncatedApiKey = apiKey.length <= opts.length ? apiKey : apiKey.slice(0, opts.length);

    return truncatedApiKey;
}

export {
    isApiKeyRevoked,
    isApiKeyDisabled,
    isApiKeyPaused,
    isApiKeyActive,
    isApiKeyVisibleToUser,
    isWorkspaceApiKey,
    generateApiKeyPrefix
};
